import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaEye, FaUserShield, FaSpinner, FaArrowLeft, FaLock, FaFileAlt, FaEdit, FaTrash } from 'react-icons/fa';
import { adminRegistrationsService, userService, mechanicGarageService, kycService, getDocumentUrl } from '../../../../services/apiService';
import { getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';
import PartnerRegistrationDetail from '../components/PartnerRegistrationDetail';
import GarageClusterVerificationPanel from '../components/GarageClusterVerificationPanel';

const SENSITIVE_KEYS = ['password', 'confirmPassword', 'confirm_password'];
const isDocPath = (v) => typeof v === 'string' && (v.startsWith('/uploads/') || v.startsWith('uploads/') || v.startsWith('http://') || v.startsWith('https://'));
const getDocValue = (v) => (v && typeof v === 'object' && v.path != null ? v.path : v);
const normalizeDoc = (d) => (typeof d === 'string' ? { path: d, label: 'Document' } : { path: d?.path || '', label: d?.label || d?.type || 'Document' });
const isSensitive = (k) => SENSITIVE_KEYS.some((sk) => String(k).toLowerCase().includes(sk.toLowerCase()));

const KYC_LABELS = {
  aadhaarFront: 'Aadhaar Front',
  aadhaarBack: 'Aadhaar Back',
  aadhaarNumber: 'Aadhaar Number',
  panUpload: 'PAN Document',
  panNumber: 'PAN Number',
  panCardDocument: 'PAN Card Document',
  gstCert: 'GST Certificate',
  gstNumber: 'GST Number',
  shopLicense: 'Shop License',
  shopPicture: 'Shop Picture',
  streetPicture: 'Street Picture',
  registrationCertificate: 'Registration Certificate',
  addressProof: 'Address Proof',
  businessLogo: 'Business Logo',
  identityDoc: 'Identity Document',
  idProof: 'ID Proof',
  companyName: 'Company Name',
  ownerName: 'Owner Name',
  storeName: 'Store / Shop Name',
  shopName: 'Shop Name',
  shopNumber: 'Shop Phone',
  companyType: 'Company Type',
  businessSpecificationLabel: 'Business Type',
  businessSpecificationLabels: 'Business Categories',
  fullAddress: 'Full Address',
  houseNo: 'House / Shop No.',
  street: 'Street',
  landmark: 'Landmark',
  city: 'City',
  pinCode: 'PIN Code',
  locationPinCode: 'GPS PIN',
  contactEmail: 'Contact Email',
  sellerCategory: 'Seller Category',
  bankAccountHolder: 'Account Holder',
  bankAccountNumber: 'Account Number',
  bankIfsc: 'IFSC',
  bankName: 'Bank Name',
  verificationStatus: 'Verification Status',
};

const PARTNER_DOC_KEYS = [
  ['registrationCertificate', 'Registration certificate'],
  ['panCardDocument', 'PAN card'],
  ['shopPicture', 'Shop picture'],
  ['streetPicture', 'Street picture'],
  ['panUpload', 'PAN document'],
  ['gstCert', 'GST certificate'],
  ['aadhaarFront', 'Aadhaar front'],
  ['aadhaarBack', 'Aadhaar back'],
  ['shopLicense', 'Shop license'],
  ['addressProof', 'Address proof'],
  ['businessLogo', 'Business logo'],
];

const REGISTRATION_SKIP_KEYS = new Set([
  'onboardingPending',
  'pinCodeMismatch',
  'businessSpecifications',
  'businessSpecificationOther',
  'latitude',
  'longitude',
  'phone',
  'businessName',
  'gstVerification',
  'bankVerification',
  'partsOnlyDeclared',
  'payoutHeld',
  'fraudReviewRequired',
  'dashboardTools',
  'sellerMode',
  'sellerModeLabel',
  'sellerOnboarding',
  'listingMode',
  'modeRevalidatedAt',
  'approximateCarCount',
  'accountNumberLast4',
  'accountHolderName',
  'ifscCode',
]);

const PARTNER_SIGNAL_KEYS = [
  'companyName', 'ownerName', 'shopName', 'storeName', 'panNumber', 'aadhaarNumber',
  'houseNo', 'street', 'city', 'pinCode', 'fullAddress', 'gstNumber', 'companyType',
  'registrationCertificate', 'panCardDocument', 'shopPicture', 'streetPicture', 'bankAccountHolder',
  'bankAccountNumber', 'bankIfsc', 'bankName', 'contactEmail', 'yardShopName', 'fullName',
];

function hasMeaningfulRegistrationData(blob) {
  if (!blob || typeof blob !== 'object') return false;
  return PARTNER_SIGNAL_KEYS.some((key) => {
    const v = blob[key];
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'boolean') return false;
    return true;
  });
}

function pickRegistrationBlob(user) {
  const role = (user?.role || '').toLowerCase();
  if (role === 'garage' && hasMeaningfulRegistrationData(user?.garageDetails)) {
    return user.garageDetails;
  }
  if (hasMeaningfulRegistrationData(user?.vendorDetails)) {
    return user.vendorDetails;
  }
  if (hasMeaningfulRegistrationData(user?.usedSellerDetails)) {
    return user.usedSellerDetails;
  }
  if (user?.mechanicDetails && hasMeaningfulRegistrationData(user.mechanicDetails)) return user.mechanicDetails;
  if (user?.shippingDetails && hasMeaningfulRegistrationData(user.shippingDetails)) return user.shippingDetails;
  return null;
}

function maskSensitiveValue(key, value) {
  const k = String(key).toLowerCase();
  const str = String(value ?? '');
  if (!str) return '—';
  if (k.includes('aadhaar') && str.length >= 4) {
    return `XXXX-XXXX-${str.slice(-4)}`;
  }
  if ((k.includes('accountnumber') || k === 'bankaccountnumber') && str.length >= 4) {
    return `••••${str.slice(-4)}`;
  }
  return str;
}

function buildRegistrationSections(user) {
  const blob = pickRegistrationBlob(user);
  if (!blob) return [];

  const business = {};
  const address = {};
  const bank = {};
  const other = {};

  Object.entries(blob).forEach(([key, raw]) => {
    if (REGISTRATION_SKIP_KEYS.has(key)) return;
    if (PARTNER_DOC_KEYS.some(([k]) => k === key)) return;
    if (raw == null || raw === '') return;
    if (typeof raw === 'object' && !Array.isArray(raw)) return;

    let value = raw;
    if (Array.isArray(raw)) {
      value = raw.filter(Boolean).join(', ');
    }
    if (key === 'aadhaarNumber' || key === 'bankAccountNumber') {
      value = maskSensitiveValue(key, value);
    }

    const lk = key.toLowerCase();
    if (['houseno', 'street', 'landmark', 'city', 'pincode', 'locationpincode', 'fulladdress'].includes(lk) || lk.includes('address')) {
      address[key] = value;
    } else if (lk.startsWith('bank') || lk.includes('ifsc') || lk.includes('account')) {
      bank[key] = value;
    } else if (['companyname', 'ownername', 'storename', 'shopname', 'shopnumber', 'companytype', 'gstnumber', 'pannumber', 'aadhaarnumber', 'businessspecificationlabel', 'businessspecificationlabels', 'sellercategory', 'contactemail', 'verificationstatus'].includes(lk) || lk.includes('business')) {
      business[key] = value;
    } else {
      other[key] = value;
    }
  });

  const sections = [];
  if (Object.keys(business).length) sections.push({ title: 'Business details', data: business });
  if (Object.keys(address).length) sections.push({ title: 'Shop address', data: address });
  if (Object.keys(bank).length) sections.push({ title: 'Bank details', data: bank });
  if (Object.keys(other).length) sections.push({ title: 'Other details', data: other });

  const addr = user?.address;
  if (addr && typeof addr === 'object') {
    const addrData = {};
    ['area', 'city', 'state', 'country', 'postalCode', 'fullAddress'].forEach((k) => {
      if (addr[k]) addrData[k] = addr[k];
    });
    if (Object.keys(addrData).length) {
      sections.push({ title: 'Saved address (profile)', data: addrData });
    }
  }

  return sections;
}

function collectRegistrationDocuments(user) {
  const blob = pickRegistrationBlob(user);
  const found = [];
  const seen = new Set();

  const add = (path, label) => {
    const p = getDocValue(path);
    if (!p || !isDocPath(p) || seen.has(p)) return;
    seen.add(p);
    found.push({ path: p, label });
  };

  if (blob) {
    PARTNER_DOC_KEYS.forEach(([key, label]) => add(blob[key], label));
    Object.entries(blob).forEach(([key, val]) => {
      const p = getDocValue(val);
      if (isDocPath(p) || (typeof val === 'string' && isDocPath(val))) {
        add(val, formatKey(key));
      }
    });
  }

  return found;
}

const formatKey = (k) => KYC_LABELS[k] || String(k).replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

const StepDataDisplay = ({ data }) => {
  if (!data || typeof data !== 'object') return <p className="text-sm text-gray-500 mt-2">—</p>;
  const entries = Object.entries(data);
  if (!entries.length) return <p className="text-sm text-gray-500 mt-2">—</p>;
  return (
    <div className="mt-3 space-y-2.5 overflow-auto max-h-64">
      {entries.map(([k, v]) => {
        const docVal = getDocValue(v);
        const isDoc = isDocPath(docVal) || (typeof v === 'string' && isDocPath(v));
        const sensitive = isSensitive(k);
        const isBool = v === true || v === false;
        const displayVal = sensitive ? '••••••••' : (typeof v === 'object' && v !== null && !Array.isArray(v) ? JSON.stringify(v) : String(v ?? '—'));
        return (
          <div key={k} className="flex items-start gap-2 py-2 border-b border-gray-100 last:border-0">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide shrink-0 min-w-[120px]">{formatKey(k)}</span>
            <span className="flex-1 min-w-0">
              {isDoc && (docVal || (typeof v === 'string' && v)) ? (
                <a href={getDocumentUrl(docVal || v)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-600 hover:text-sky-700 hover:underline">
                  <FaFileAlt className="text-xs" /> View document
                </a>
              ) : isBool ? (
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${v ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {v ? <FaCheckCircle className="text-emerald-500" /> : <FaTimesCircle className="text-gray-400" />}
                  {v ? 'Yes' : 'No'}
                </span>
              ) : sensitive ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                  <FaLock className="text-gray-400 text-xs" />
                  {displayVal}
                </span>
              ) : (
                <span className="text-sm text-gray-800 break-words">{displayVal || '—'}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const KYC = () => {
  const [view, setView] = useState('list');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycStatusFilter, setKycStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [kycRequests, setKycRequests] = useState([]);
  const [userKycList, setUserKycList] = useState([]);
  const [fullUserKycList, setFullUserKycList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectFields, setRejectFields] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [userDetails, setUserDetails] = useState(null);
  const [kycDetails, setKycDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', phoneCode: '+91', kycStatus: 'Pending' });

  const KYC_ROLES = ['vendor', 'delivery', 'mechanics', 'garage', 'used_vendor'];
  const USER_KYC_ROLES = ['vendor', 'mechanics', 'garage', 'shipping', 'used_vendor', 'used_pending'];
  const getRoleLabel = (role) => ({
    vendor: 'Vendor',
    delivery: 'Shipping',
    mechanics: 'Mechanic',
    garage: 'Garage',
    shipping: 'Shipping',
    used_vendor: 'Used Parts Seller',
    used_pending: 'Used Parts Seller (pending)',
  }[(role || '').toLowerCase()] || (role || '—'));

  const fetchRegistrations = async () => {
    try {
      const filters = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (kycStatusFilter !== 'all') filters.kycStatus = kycStatusFilter;
      if (roleFilter !== 'all') filters.role = roleFilter;
      const list = await adminRegistrationsService.list(filters);
      const filtered = (Array.isArray(list) ? list : []).filter(
        (r) => roleFilter === 'all' ? KYC_ROLES.includes((r.role || '').toString().toLowerCase()) : true
      );
      setKycRequests(filtered);
    } catch (err) {
      toast.error(err.message || 'Failed to load registrations');
      setKycRequests([]);
    }
  };

  const fetchUserKycList = async () => {
    try {
      const response = await userService.getAllUsers();
      const raw = response?.data ?? response?.users ?? response;
      const users = Array.isArray(raw) ? raw : (raw?.users ?? raw?.data ?? []);
      const roleMatch = (u) => {
        const r = (u.role || '').toLowerCase();
        if (roleFilter === 'all') return USER_KYC_ROLES.includes(r);
        if (roleFilter === 'delivery') return r === 'shipping';
        return r === roleFilter.toLowerCase();
      };
      const list = (Array.isArray(users) ? users : [])
        .filter((u) => USER_KYC_ROLES.includes((u.role || '').toLowerCase()))
        .filter(roleMatch)
        .map((u) => ({
          id: u._id || u.id,
          _id: u._id || u.id,
          name: u.usedSellerDetails?.yardShopName || u.vendorDetails?.storeName || u.vendorDetails?.businessName || u.mechanicDetails?.name || u.garageDetails?.garageName || u.shippingDetails?.name || getProfileDisplayName(u) || '',
          email: getProfileDisplayEmail(u) || '',
          role: u.role,
          kycStatus: u.kycStatus || 'Pending'
        }));
      setFullUserKycList(list);
      const kycFilter = (kycStatusFilter || '').toLowerCase();
      const matchKyc = (r) => {
        const s = (r.kycStatus || 'Pending').toLowerCase();
        if (kycFilter === 'all') return true;
        if (kycFilter === 'pending') return s === 'pending';
        if (kycFilter === 'approved' || kycFilter === 'verified') return s === 'verified';
        if (kycFilter === 'rejected') return s === 'rejected';
        return s === kycFilter;
      };
      setUserKycList(kycFilter === 'all' ? list : list.filter(matchKyc));
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
      setUserKycList([]);
      setFullUserKycList([]);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchRegistrations(), fetchUserKycList()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [statusFilter, kycStatusFilter, roleFilter]);

  const handleUserKycChange = async (user, newKycStatus) => {
    const id = user.id || user._id;
    const role = (user.role || '').toLowerCase();
    try {
      if (role === 'mechanics') {
        await mechanicGarageService.updateMechanic(id, { kycStatus: newKycStatus });
      } else if (role === 'garage') {
        await mechanicGarageService.updateGarage(id, { kycStatus: newKycStatus });
      } else {
        await userService.updateUser(id, { kycStatus: newKycStatus });
      }
      setUserKycList((prev) => prev.map((u) => (u.id === id || u._id === id ? { ...u, kycStatus: newKycStatus } : u)));
      setFullUserKycList((prev) => prev.map((u) => (u.id === id || u._id === id ? { ...u, kycStatus: newKycStatus } : u)));
      toast.success('KYC status updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update KYC status');
    }
  };

  const filteredRequests = kycRequests;
  const filteredUserList = userKycList;

  const handleView = async (u) => {
    setSelectedUser(u);
    setViewMode('view');
    setDetailLoading(true);
    setUserDetails(null);
    setKycDetails(null);
    try {
      const id = u.id || u._id;
      const [userRes, kycRes] = await Promise.all([
        userService.getUserById(id).catch(() => null),
        kycService.getByUserId(id).catch(() => null)
      ]);
      setUserDetails(userRes?.data || userRes || null);
      setKycDetails(kycRes || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditOpen = async (u) => {
    setSelectedUser(u);
    setViewMode('edit');
    setDetailLoading(true);
    setUserDetails(null);
    setKycDetails(null);
    setEditForm({
      name: u.name || '',
      email: u.email || '',
      phone: u.phone || '',
      phoneCode: u.phoneCode || '+91',
      kycStatus: u.kycStatus || 'Pending'
    });
    try {
      const id = u.id || u._id;
      const [userRes, kycRes] = await Promise.all([
        userService.getUserById(id).catch(() => null),
        kycService.getByUserId(id).catch(() => null)
      ]);
      const user = userRes?.data || userRes || u;
      setUserDetails(user);
      setKycDetails(kycRes || null);
      const name = user.usedSellerDetails?.yardShopName || user.vendorDetails?.storeName || user.vendorDetails?.businessName || user.mechanicDetails?.name || user.garageDetails?.garageName || user.shippingDetails?.name || user.name || u.name || '';
      setEditForm((f) => ({
        ...f,
        name: name || f.name,
        email: user.email ?? f.email,
        phone: user.phone ?? f.phone,
        phoneCode: user.phoneCode ?? f.phoneCode,
        kycStatus: user.kycStatus || f.kycStatus || 'Pending'
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    const id = selectedUser.id || selectedUser._id;
    const role = (selectedUser.role || '').toLowerCase();
    try {
      setActionLoading(true);
      const payload = { ...editForm };
      if (role === 'mechanics') {
        await mechanicGarageService.updateMechanic(id, payload);
      } else if (role === 'garage') {
        await mechanicGarageService.updateGarage(id, payload);
      } else {
        await userService.updateUser(id, payload);
      }
      setUserKycList((prev) => prev.map((x) => (x.id === id || x._id === id ? { ...x, ...editForm } : x)));
      setFullUserKycList((prev) => prev.map((x) => (x.id === id || x._id === id ? { ...x, ...editForm } : x)));
      toast.success('Updated successfully');
      setViewMode('list');
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (u) => {
    const name = u.name || getRoleLabel(u.role) || 'this user';
    if (!window.confirm(`Are you sure you want to delete ${name}? This cannot be undone.`)) return;
    const id = u.id || u._id;
    const role = (u.role || '').toLowerCase();
    try {
      setActionLoading(true);
      if (role === 'mechanics') {
        await mechanicGarageService.deleteMechanic(id);
      } else if (role === 'garage') {
        await mechanicGarageService.deleteGarage(id);
      } else {
        await userService.deleteUser(id);
      }
      toast.success('Deleted successfully');
      setUserKycList((prev) => prev.filter((x) => (x.id || x._id) !== id));
      setFullUserKycList((prev) => prev.filter((x) => (x.id || x._id) !== id));
      if (selectedUser && (selectedUser.id === id || selectedUser._id === id)) {
        setViewMode('list');
        setSelectedUser(null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAction = (type, req) => {
    setSelectedRequest(req);
    if (type === 'view') {
      setView('details');
    } else if (type === 'approve') {
      if (window.confirm(`Approve registration for ${req.registrationId} (${getRoleLabel(req.role)})?`)) {
        handleApprove(req.registrationId);
      }
    } else if (type === 'reject') {
      setRejectReason('');
      setRejectFields('');
      setShowRejectModal(true);
    }
  };

  const handleApprove = async (registrationId) => {
    try {
      setActionLoading(true);
      await adminRegistrationsService.approve(registrationId);
      toast.success('Registration approved');
      setView('list');
      setSelectedRequest(null);
      fetchRegistrations();
    } catch (err) {
      toast.error(err.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedRequest?.registrationId) return;
    try {
      setActionLoading(true);
      const fields = rejectFields.trim() ? rejectFields.split(',').map(f => f.trim()).filter(Boolean) : [];
      await adminRegistrationsService.reject(selectedRequest.registrationId, rejectReason, fields);
      toast.success('Registration rejected');
      setShowRejectModal(false);
      setView('list');
      setSelectedRequest(null);
      setRejectReason('');
      setRejectFields('');
      fetchRegistrations();
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const displayStatus = (req) => req.status || req.kycStatus || 'pending';
  const displayDate = (date) => date ? new Date(date).toLocaleDateString() : '—';
  const stepCount = (req) => (req.steps && req.steps.length) || 0;

  if (view === 'details' && selectedRequest) {
    const steps = selectedRequest.steps || [];
    const status = displayStatus(selectedRequest);
    const statusStyles = {
      approved: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-500/10 text-red-700 border-red-200',
      submitted: 'bg-amber-500/10 text-amber-700 border-amber-200',
      under_review: 'bg-sky-500/10 text-sky-700 border-sky-200',
      pending: 'bg-amber-500/10 text-amber-700 border-amber-200',
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      resubmission_required: 'bg-orange-500/10 text-orange-700 border-orange-200',
    };
    const statusClass = statusStyles[status] || 'bg-gray-100 text-gray-700 border-gray-200';
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">KYC / Registration</h1>
          <button
            type="button"
            onClick={() => { setView('list'); setSelectedRequest(null); }}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6 border-b border-gray-100">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center shrink-0 bg-sky-50 text-sky-600 ring-2 ring-sky-100">
              <FaUserShield className="w-8 h-8 sm:w-10 sm:h-10" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-gray-900 truncate">{selectedRequest.registrationId}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-sky-600 uppercase tracking-wide">{getRoleLabel(selectedRequest.role)}</span>
                <span className="text-gray-300">·</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${statusClass}`}>
                  {status.replace(/_/g, ' ')}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Submitted {displayDate(selectedRequest.submittedAt)}</p>
            </div>
          </div>

          <div className="p-6 border-b border-gray-100 bg-sky-50/30">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">KYC Status & Documents</h3>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-gray-600">KYC Status:</span>
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold border ${statusClass}`}>
                {status.replace(/_/g, ' ')}
              </span>
            </div>
            {(() => {
              const docEntries = [];
              steps.forEach((s) => {
                const d = s.stepData || {};
                Object.entries(d).forEach(([key, val]) => {
                  const docVal = getDocValue(val);
                  if (isDocPath(docVal) || (typeof val === 'string' && isDocPath(val))) {
                    docEntries.push({ key, url: docVal || val, label: formatKey(key) });
                  }
                });
              });
              if (docEntries.length === 0) return <p className="text-sm text-gray-500">No documents uploaded</p>;
              return (
                <div className="flex flex-wrap gap-2">
                  {docEntries.map(({ key, url, label }) => (
                    <a key={key} href={getDocumentUrl(url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-white text-sky-700 text-sm font-medium hover:bg-sky-50">
                      <FaFileAlt /> {label}
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            <div className="lg:col-span-3 p-6 border-b lg:border-b-0 lg:border-r border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">KYC Details (all steps)</h3>
              {steps.length ? (
                <div className="space-y-4">
                  {steps.map((s, i) => (
                    <div key={i} className="rounded-lg border border-gray-100 bg-gray-50/50 overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-100/80 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Step {s.stepNumber}</span>
                      </div>
                      <div className="px-4 py-3">
                        <StepDataDisplay data={s.stepData || {}} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4">No KYC details</p>
              )}
            </div>
            <div className="lg:col-span-2 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Status</h3>
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Current:</span>{' '}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium capitalize ${statusClass}`}>
                    {status.replace(/_/g, ' ')}
                  </span>
                </p>
                {selectedRequest.rejectionReason && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-gray-700">Reason:</span> {selectedRequest.rejectionReason}
                  </p>
                )}
              </div>
            </div>
          </div>

          {(selectedRequest.status === 'submitted' || selectedRequest.status === 'under_review' || selectedRequest.kycStatus === 'submitted') && (
            <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => handleAction('approve', selectedRequest)}
                disabled={actionLoading}
                className="flex-1 py-3 px-4 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 active:scale-[0.99] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                Approve
              </button>
              <button
                type="button"
                onClick={() => handleAction('reject', selectedRequest)}
                className="flex-1 py-3 px-4 bg-white border border-red-200 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50 active:scale-[0.99] transition flex items-center justify-center gap-2"
              >
                <FaTimesCircle /> Reject
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (viewMode === 'view' && selectedUser) {
    const u = userDetails || selectedUser;
    const kyc = kycDetails;
    const hasOnboarding = Boolean(u?.onboardingComplete === false || u?.vendorDetails?.verificationStatus === 'pending_review' || u?.vendorDetails?.onboardingPending);
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900">KYC Details</h1>
          <button
            type="button"
            onClick={() => { setViewMode('list'); setSelectedUser(null); setUserDetails(null); setKycDetails(null); }}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        </div>
        {detailLoading ? (
          <div className="flex justify-center items-center py-16">
            <FaSpinner className="animate-spin text-sky-600 text-3xl" />
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">User & KYC</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Name</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{u.name || u.usedSellerDetails?.yardShopName || u.vendorDetails?.storeName || u.mechanicDetails?.name || u.garageDetails?.garageName || u.shippingDetails?.name || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Email</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{getProfileDisplayEmail(u) || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Phone</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{(u.phoneCode || '') + (u.phone ? ' ' + u.phone : '') || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Role</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{getRoleLabel(u.role) || ''}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">KYC Status</p>
                    <p className="mt-0.5">
                      <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold ${(u.kycStatus || kyc?.status || 'Pending') === 'Verified' ? 'bg-emerald-50 text-emerald-700' : (u.kycStatus || kyc?.status) === 'Rejected' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {u.kycStatus || kyc?.status || 'Pending'}
                      </span>
                    </p>
                  </div>
                  {hasOnboarding && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase">Application</p>
                      <p className="mt-0.5">
                        <span className="inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold bg-sky-50 text-sky-700">
                          Pending review
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6">
                <PartnerRegistrationDetail user={u} />
              </div>
            </div>

            {(u.role || '').toLowerCase() === 'garage' && (
              <GarageClusterVerificationPanel
                garageId={u.id || u._id}
                garageDetails={u.garageDetails || {}}
                onUpdated={(updated) => {
                  const user = updated?.garage || updated;
                  const gd = user?.garageDetails;
                  if (!gd) return;
                  setUserDetails((prev) => ({ ...(prev || u), garageDetails: gd }));
                  setSelectedUser((prev) => ({ ...(prev || u), garageDetails: gd }));
                }}
              />
            )}

            <div className="flex gap-3">
              <button type="button" onClick={() => handleEditOpen(selectedUser)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700">
                <FaEdit /> Edit
              </button>
              <button type="button" onClick={() => handleDelete(selectedUser)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700">
                <FaTrash /> Delete
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  if (viewMode === 'edit' && selectedUser) {
    const rawEditDocs = (kycDetails && kycDetails.documents) ? (Array.isArray(kycDetails.documents) ? kycDetails.documents : []) : [];
    const editDocs = rawEditDocs.map(normalizeDoc).filter((d) => d.path);
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900">Edit KYC User</h1>
          <button type="button" onClick={() => { setViewMode('list'); setSelectedUser(null); setUserDetails(null); setKycDetails(null); }} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-100 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
            <FaArrowLeft /> Back
          </button>
        </div>
        {detailLoading ? (
          <div className="flex justify-center items-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
            <FaSpinner className="animate-spin text-sky-600 text-3xl" />
          </div>
        ) : (
          <form onSubmit={handleEditSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Name</label>
              <input type="text" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500" placeholder="Name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Email</label>
              <input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500" placeholder="Email" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Phone Code</label>
                <input type="text" value={editForm.phoneCode} onChange={(e) => setEditForm((f) => ({ ...f, phoneCode: e.target.value }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500" placeholder="+91" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Phone</label>
                <input type="text" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500" placeholder="Phone" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">KYC Status</label>
              <select value={editForm.kycStatus} onChange={(e) => setEditForm((f) => ({ ...f, kycStatus: e.target.value }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white">
                <option value="Pending">Pending</option>
                <option value="Verified">Verified</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Documents</h3>
              {editDocs.length === 0 ? (
                <p className="text-sm text-gray-400 min-h-[2rem]">&nbsp;</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {editDocs.map((d, i) => (
                    <a key={i} href={getDocumentUrl(d.path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-sm font-medium hover:bg-sky-100">
                      <FaFileAlt className="text-xs" /> {d.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={actionLoading} className="flex-1 py-3 px-4 bg-sky-600 text-white text-sm font-semibold rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading && <FaSpinner className="animate-spin" />} Save
              </button>
              <button type="button" onClick={() => setViewMode('list')} className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h2 className="text-lg font-bold text-gray-900">KYC / Registrations</h2>
        <p className="text-xs text-gray-600 mt-1">Vendor, Mechanic, Garage, Shipping — review and approve or reject submitted KYC</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-amber-100 rounded-lg p-4 border border-amber-200">
          <p className="text-sm text-amber-700 mb-1">Pending</p>
          <p className="text-xl font-bold text-amber-900">{fullUserKycList.filter(r => (r.kycStatus || 'Pending') === 'Pending').length}</p>
        </div>
        <div className="bg-yellow-100 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-700 mb-1">Submitted</p>
          <p className="text-xl font-bold text-yellow-900">{kycRequests.filter(r => (r.status || r.kycStatus) === 'submitted').length}</p>
        </div>
        <div className="bg-green-100 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-green-700 mb-1">Verified</p>
          <p className="text-xl font-bold text-green-900">{fullUserKycList.filter(r => (r.kycStatus || '') === 'Verified').length}</p>
        </div>
        <div className="bg-red-100 rounded-lg p-4 border border-red-200">
          <p className="text-sm text-red-700 mb-1">Rejected</p>
          <p className="text-xl font-bold text-red-900">{fullUserKycList.filter(r => (r.kycStatus || '') === 'Rejected').length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
        <p className="text-xs text-gray-500 mb-3">Table shows Vendor, Mechanic, Garage, Shipping users. Change KYC status from the dropdown. Role & KYC filters apply together.</p>
        <div className="flex flex-wrap gap-4 items-center">
          <select
            value={kycStatusFilter}
            onChange={(e) => setKycStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All KYC Status</option>
            <option value="pending">Pending</option>
            <option value="Verified">Verified</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="vendor">Vendor</option>
            <option value="delivery">Shipping</option>
            <option value="mechanics">Mechanic</option>
            <option value="garage">Garage</option>
            <option value="used_vendor">Used Parts Seller</option>
            <option value="used_pending">Used Parts Seller (pending)</option>
          </select>
        </div>
      </div>

      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Reject Registration</h3>
            <p className="text-sm text-gray-600">Registration ID: {selectedRequest.registrationId}</p>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Reason (optional)</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                rows={3}
                placeholder="Reason for rejection..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Fields to fix (comma-separated, optional)</label>
              <input
                type="text"
                value={rejectFields}
                onChange={(e) => setRejectFields(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g. panNumber, aadhaarFront"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setShowRejectModal(false); setSelectedRequest(null); setRejectReason(''); setRejectFields(''); }}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={actionLoading}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? <FaSpinner className="animate-spin" /> : null}
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FaSpinner className="animate-spin text-blue-600 text-3xl" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">KYC Status</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUserList.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {loading ? null : 'No Vendor, Mechanic, Garage or Shipping users found. Add users from their respective pages first.'}
                    </td>
                  </tr>
                ) : (
                  filteredUserList.map((u) => (
                    <tr key={u.id || u._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="font-semibold text-gray-900">{u.name || ''}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{u.email || ''}</td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{getRoleLabel(u.role)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={u.kycStatus || 'Pending'}
                          onChange={(e) => handleUserKycChange(u, e.target.value)}
                          className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border cursor-pointer ${
                            (u.kycStatus || 'Pending') === 'Verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                            (u.kycStatus || 'Pending') === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' :
                            'bg-amber-50 border-amber-200 text-amber-800'
                          }`}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Verified">Verified</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => handleView(u)} className="p-2 text-sky-600 hover:bg-sky-50 rounded-lg transition" title="View">
                            <FaEye className="text-sm" />
                          </button>
                          <button type="button" onClick={() => handleEditOpen(u)} className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition" title="Edit">
                            <FaEdit className="text-sm" />
                          </button>
                          <button type="button" onClick={() => handleDelete(u)} disabled={actionLoading} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50" title="Delete">
                            <FaTrash className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYC;